import type { SetDropdownStateRef } from "@hoylu/client-common";
import Color from "color";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ShareWorkspaceDialogSections } from "../../../enums/ShareWorkspaceDialogSections";
import { sendMessageToWorkspace } from "../../../post-message-dispatch";
import type { AppState } from "../../../state/root.reducer";
import { userToken as selectUserToken } from "../../../state/user/user.selector";
import { selectProjectById } from "../../../state/workspaces/projects.selector";
import {
  cancelDashboardOption,
  removeWorkspacePassword,
  setWorkspacePassword,
} from "../../../state/workspaces/workspaces.actions";
import {
  isWorkspaceLimited,
  selectFullscreenWorkspaceId,
  selectPublicPermissionLevel,
  selectSelectedWorkspace,
  selectWorkspacesEmailPreset,
  selectWorkspacesShowOverlay,
} from "../../../state/workspaces/workspaces.selector";
import { useI18n } from "../../../utils/hooks/use.i18n";
import {
  formatWorkspaceId,
  isOrganizationAdmin,
} from "../../../utils/sharing.helpers";
import { Modal } from "../Modal";
import { ModalHeader } from "../Modal.Header";
import { CreditLimitSection } from "./CreditLimitSection";
import { PermissionsSection } from "./PermissionsSection";
import { ProjectPill } from "./ProjectPill";
import Styles from "./ShareWorkspaceDialog.module.css";
import { InfoSection } from "./sharing.enums";
import { TemplateInfoSection } from "./TemplateInfoSection";
import { WorkspaceInfoSection } from "./WorkspaceInfoSection";
import { MODE } from "./WorkspacePassword";

type ShareWorkspaceDialogProps = {
  openSection?: ShareWorkspaceDialogSections;
};

export const ShareWorkspaceDialog = ({
  openSection,
}: ShareWorkspaceDialogProps) => {
  const dispatch = useDispatch();

  const t = useI18n("SHARE_WORKSPACE_DIALOG.");

  const selectedMyWorkspaceMetadata = useSelector(selectSelectedWorkspace);
  const project = useSelector((state: AppState) =>
    selectProjectById(
      state,
      selectedMyWorkspaceMetadata?.containerId ?? undefined,
    ),
  );
  const fullscreenWorkspaceId = useSelector(selectFullscreenWorkspaceId);
  const emailPreset = useSelector(selectWorkspacesEmailPreset);
  const showOverlay = useSelector(selectWorkspacesShowOverlay);
  const userToken = useSelector(selectUserToken);
  const isLimited = useSelector((state: AppState) =>
    isWorkspaceLimited(state, selectedMyWorkspaceMetadata?.workspaceId),
  );
  const publicPermissionLevel = useSelector(selectPublicPermissionLevel);

  const isOrgAdmin = isOrganizationAdmin(userToken);

  const setDropdownStateRef = useRef<SetDropdownStateRef>(null);
  const isAdmin = !!selectedMyWorkspaceMetadata?.myPermissions.canAdmin;
  const isReadOnly = !selectedMyWorkspaceMetadata?.myPermissions.canWrite;
  const workspaceIsFullscreen =
    selectedMyWorkspaceMetadata?.workspaceId === fullscreenWorkspaceId;

  const showWarningSection =
    isLimited && !isReadOnly && !selectedMyWorkspaceMetadata?.containerId;

  const [infoSectionType, setInfoSectionType] = useState<InfoSection>(() =>
    selectedMyWorkspaceMetadata?.templateId
      ? InfoSection.TEMPLATE
      : InfoSection.WORKSPACE,
  );
  const [keepTemplateInfoOpen, setKeepTemplateInfoOpen] = useState(false);

  const changeInfoSection = (infoSection: InfoSection) => {
    setInfoSectionType(infoSection);
    if (infoSection === InfoSection.TEMPLATE) {
      setKeepTemplateInfoOpen(true);
    }
  };

  const close = () => {
    if (selectedMyWorkspaceMetadata?.workspaceId) {
      sendMessageToWorkspace(selectedMyWorkspaceMetadata.workspaceId, {
        action: "PERMISSIONS_CHANGED",
      });
    }
    dispatch(cancelDashboardOption());
  };

  const arePermissionsAndUsersLoaded = !!publicPermissionLevel;

  const handleWorkspacePassword = (
    password: string,
    currentPassword: string,
    mode: string,
  ): void => {
    if (!selectedMyWorkspaceMetadata?.workspaceId) return;

    switch (mode) {
      case MODE.ADD:
      case MODE.CHANGE:
        dispatch(
          setWorkspacePassword.request({
            workspaceId: selectedMyWorkspaceMetadata.workspaceId,
            currPassword: currentPassword,
            newPassword: password,
          }),
        );
        break;
      case MODE.REMOVE:
        dispatch(
          removeWorkspacePassword.request({
            workspaceId: selectedMyWorkspaceMetadata.workspaceId,
            currPassword: password,
          }),
        );
        break;
    }
  };

  const isActiveSection = useCallback(
    (...sections: ShareWorkspaceDialogSections[]) =>
      !!openSection && sections.includes(openSection),
    [openSection],
  );

  useEffect(() => {
    // NOTE: Closes share dialog when it was automatically opened for a newly created workspace but user returned to the dashboard. It will also close share dialog, when rerendering of live workspaces will trigger share dialog, which can happen when user left newly created workspace without making any changes after creation.
    if (
      isActiveSection(ShareWorkspaceDialogSections.EVERY) &&
      !workspaceIsFullscreen
    ) {
      close();
    }
  }, [isActiveSection, workspaceIsFullscreen]);

  const backgroundColor = Color(project?.color || "lightgrey");

  return (
    <Modal
      backgroundColor={
        selectedMyWorkspaceMetadata?.containerId
          ? backgroundColor.darken(0.5).hex()
          : undefined
      }
      components={{
        Header: (
          <ModalHeader onClose={close}>
            {project && <ProjectPill project={project} />}
            <span className={Styles.modalHeader}>
              {t(
                "SHARE_THIS_WORKSPACE",
                formatWorkspaceId(
                  selectedMyWorkspaceMetadata?.workspaceId ?? "",
                ),
              )}
            </span>
          </ModalHeader>
        ),
      }}
      handleOutsideClick={close}
      showOverlay={showOverlay}
    >
      <div className={Styles.shareWorkspaceDialog} data-test-id="share-dialog">
        <div className={Styles.shareWorkspaceDialogContent}>
          {showWarningSection && (
            <CreditLimitSection
              openDropdown={() =>
                setDropdownStateRef.current?.setDropdownState(true)
              }
            />
          )}
          {infoSectionType === InfoSection.WORKSPACE ? (
            <WorkspaceInfoSection
              handleChangeSectionType={changeInfoSection}
              initiallyOpen={isActiveSection(
                ShareWorkspaceDialogSections.INFO,
                ShareWorkspaceDialogSections.EVERY,
              )}
              isOrgAdmin={isOrgAdmin}
              setDropdownStateRef={setDropdownStateRef}
              shouldSelectTitle={isActiveSection(
                ShareWorkspaceDialogSections.EVERY,
              )}
            />
          ) : (
            <TemplateInfoSection
              handleChangeSectionType={changeInfoSection}
              initiallyOpen={
                isActiveSection(ShareWorkspaceDialogSections.INFO) ||
                keepTemplateInfoOpen
              }
              isOrgAdmin={isOrgAdmin}
              setDropdownStateRef={setDropdownStateRef}
            />
          )}
          {arePermissionsAndUsersLoaded && (
            <PermissionsSection
              emailPreset={{ emails: emailPreset ?? "" }}
              handleWorkspacePassword={handleWorkspacePassword}
              initiallyOpen={isActiveSection(
                ShareWorkspaceDialogSections.PERMISSION,
                ShareWorkspaceDialogSections.EVERY,
              )}
              isAdmin={isAdmin}
              sectionTitle={t(
                project
                  ? "PERMISSIONS_SECTION.TITLE_PROJECT_WORKSPACE"
                  : "PERMISSIONS_SECTION.TITLE",
              )}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ShareWorkspaceDialog;
